import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { FiArrowLeft, FiCheckCircle, FiLoader, FiMail } from "react-icons/fi";
import Button from "components/Button";
import Logo from "components/common/Logo";
import FormTextInput from "components/form/FormTextInput";
import AuthSplitLayout from "layouts/AuthSplitLayout";
import { api } from "lib/api";
import swtAlert from "lib/sweetAlert";
import { validateEmail } from "lib/validation";
import { isAuthenticated, isSuccessResponse } from "routes/auth";
import { DEFAULT_APP_PATH } from "routes/paths";
import { apiURl } from "store/actions";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [serverMessage, setServerMessage] = useState("");

  if (isAuthenticated()) {
    return <Navigate to={DEFAULT_APP_PATH} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateEmail(email);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");

    setIsLoading(true);
    try {
      const resp = await api.post(
        apiURl.forgotPassword,
        { email: email.trim() },
        { auth: false }
      );

      if (!isSuccessResponse(resp)) {
        const msg = resp?.message || "Couldn't send reset link";
        setError(msg);
        swtAlert.error("Reset Link Failed", msg);
        return;
      }

      setSentTo(email.trim());
      setServerMessage(
        resp?.message ||
          "If that email is registered, a password reset link has been sent."
      );
      swtAlert.success("Reset Link Sent", resp?.message || "Reset link sent");
    } catch (err) {
      const msg = err?.message || "Couldn't send reset link";
      setError(msg);
      swtAlert.error("Reset Link Failed", msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthSplitLayout
      heroTitle="Reset access safely"
      heroDescription="We will email you a secure link to create a new password for your backoffice account."
    >
      <div className="w-full max-w-sm space-y-6">
        <Logo showText text="Fortify" logoClassName="h-7 w-7" textClassName="text-base font-bold text-slate-900" />

        <div>
          <h1 className="text-3xl font-bold text-slate-900">Forgot password?</h1>
          <p className="mt-2 text-sm text-slate-500">
            Enter the email on your account and we&apos;ll send you a reset link.
          </p>
        </div>

        {sentTo ? (
          <ResetLinkSentPanel
            email={sentTo}
            message={serverMessage}
            onResend={() => {
              setSentTo("");
              setServerMessage("");
            }}
          />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <FormTextInput
              label="Email"
              name="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError("");
              }}
              inputClassName="bg-slate-100"
              error={error}
              disabled={isLoading}
            />

            <Button
              type="submit"
              className="flex w-full items-center justify-center gap-2 py-3"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <FiLoader className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                  Sending link...
                </>
              ) : (
                <>
                  <FiMail className="h-4 w-4 shrink-0" aria-hidden />
                  Send reset link
                </>
              )}
            </Button>
          </form>
        )}

        <Link
          to="/login"
          className="inline-flex w-full items-center justify-center gap-2 text-sm font-medium text-slate-600 underline decoration-slate-600"
        >
          <FiArrowLeft className="h-4 w-4" aria-hidden />
          Back to login
        </Link>
      </div>
    </AuthSplitLayout>
  );
}

function ResetLinkSentPanel({ email, message, onResend }) {
  return (
    <div className="space-y-4 rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-center text-sm text-emerald-900">
      <FiCheckCircle className="mx-auto h-10 w-10 text-emerald-600" aria-hidden />
      <h2 className="text-base font-semibold">Check your email</h2>
      <p>{message}</p>
      <p className="text-xs text-emerald-800">
        We sent the link to <span className="font-medium">{email}</span>. The link expires soon—use it when you can.
      </p>
      <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-center">
        <Button variant="secondary" type="button" className="w-full sm:w-auto" onClick={onResend}>
          Use a different email
        </Button>
        <Link
          to="/login"
          className="inline-flex w-full items-center justify-center rounded-lg bg-brand-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-800 sm:w-auto"
        >
          Back to login
        </Link>
      </div>
    </div>
  );
}
